import React, { useState, useEffect } from "react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { MaskLine } from "../lib/anim";
import { HERO, SHOWREEL_SLIDES } from "../data/content";
import { useMedia } from "../context/MediaContext";
import { ArrowDown, ArrowRight } from "lucide-react";

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

// Showreel de démonstration : images d'industries qui défilent (crossfade + Ken Burns)
const DemoShowreel = () => {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((v) => (v + 1) % SHOWREEL_SLIDES.length), 2400);
    return () => clearInterval(t);
  }, []);
  const slide = SHOWREEL_SLIDES[i];
  return (
    <div className="absolute inset-0">
      <AnimatePresence mode="sync">
        <motion.img
          key={slide.img}
          src={slide.img}
          alt={slide.label}
          initial={{ opacity: 0, scale: 1.08 }}
          animate={{ opacity: 1, scale: 1.2 }}
          exit={{ opacity: 0 }}
          transition={{ opacity: { duration: 1.1 }, scale: { duration: 2.6, ease: "easeOut" } }}
          className="absolute inset-0 w-full h-full object-cover"
        />
      </AnimatePresence>
      <div className="absolute bottom-28 lg:bottom-32 right-6 lg:right-10 z-20 text-right">
        <span className="eyebrow text-[9px] text-[#D4AF37]">En ce moment</span>
        <AnimatePresence mode="wait">
          <motion.p
            key={slide.label}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.5 }}
            className="font-serif italic text-2xl lg:text-3xl text-[#F5F5F0]"
          >
            {slide.label}
          </motion.p>
        </AnimatePresence>
      </div>
    </div>
  );
};

const Hero = () => {
  const { hero } = useMedia();
  const videos = hero.filter((m) => m.kind === "video");
  const images = hero.filter((m) => m.kind === "image");
  const hasReal = videos.length > 0 || images.length > 0;
  const [idx, setIdx] = useState(0);

  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 700], [0, 200]);
  const opacity = useTransform(scrollY, [0, 500], [1, 0]);

  const renderBackground = () => {
    if (videos.length > 0) {
      const current = videos[idx % videos.length];
      return (
        <video
          key={current.id}
          data-testid="hero-showreel-video"
          className="absolute inset-0 w-full h-full object-cover"
          autoPlay muted playsInline
          loop={videos.length === 1}
          onEnded={() => videos.length > 1 && setIdx((v) => (v + 1) % videos.length)}
          poster={current.poster || images[0]?.url}
        >
          <source src={current.url} type="video/mp4" />
        </video>
      );
    }
    if (images.length > 0) {
      return <img src={images[0].url} alt="Showreel VF Création" className="kenburns absolute inset-0 w-full h-full object-cover" />;
    }
    return <DemoShowreel />;
  };

  return (
    <section id="hero" data-testid="hero-section" className="relative h-[100svh] w-full overflow-hidden bg-black">
      <motion.div style={{ y }} className="absolute inset-0">
        {renderBackground()}
        <div className="absolute inset-0 bg-black/60" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-[#050505]/50" />
      </motion.div>

      {!hasReal && (
        <button
          data-testid="hero-demo-badge"
          onClick={() => scrollTo("portfolio")}
          className="absolute top-24 right-6 lg:right-10 z-20 eyebrow text-[9px] border border-[#D4AF37]/40 text-[#D4AF37] rounded-full px-3 py-1 bg-black/40 backdrop-blur-sm hover:bg-[#D4AF37] hover:text-black transition-colors duration-300"
        >
          Découvrir le showreel
        </button>
      )}

      <motion.div style={{ opacity }} className="relative z-10 h-full max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col justify-end pb-24 lg:pb-28">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1, duration: 1 }} className="eyebrow mb-6">
          {HERO.eyebrow}
        </motion.p>

        <h1 className="font-serif font-light text-[#F5F5F0] leading-[0.95] text-4xl sm:text-6xl lg:text-8xl xl:text-[7.5rem] max-w-5xl">
          <MaskLine delay={0.35}>{HERO.titleTop}</MaskLine>
          <MaskLine delay={0.5} className="italic gold-text">{HERO.titleBottom}</MaskLine>
        </h1>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.9 }} className="mt-8 flex flex-col lg:flex-row lg:items-end gap-8">
          <p className="text-[#C9C9C4] max-w-xl text-base sm:text-lg leading-relaxed">
            {HERO.subtitle}
          </p>
          <div className="flex flex-wrap gap-3 shrink-0">
            <button data-testid="hero-cta-devis" onClick={() => scrollTo("devis")} className="group inline-flex items-center gap-3 bg-[#D4AF37] text-black rounded-full pl-6 pr-2 py-2 text-sm font-medium tracking-wide hover:bg-[#F3E19B] transition-colors duration-300">
              Demander un devis
              <span className="grid place-items-center w-9 h-9 rounded-full bg-black text-[#D4AF37] group-hover:translate-x-1 transition-transform duration-300"><ArrowRight size={16} /></span>
            </button>
            <button data-testid="hero-cta-portfolio" onClick={() => scrollTo("portfolio")} className="inline-flex items-center gap-2 border border-white/25 text-[#F5F5F0] rounded-full px-6 py-2.5 text-sm tracking-wide hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors duration-300">
              Voir les réalisations
            </button>
          </div>
        </motion.div>
      </motion.div>

      <motion.button onClick={() => scrollTo("apropos")} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4 }} className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-[#A1A1AA] hidden lg:flex flex-col items-center gap-2" aria-label="Défiler">
        <span className="eyebrow text-[9px]">Défiler</span>
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}><ArrowDown size={16} /></motion.span>
      </motion.button>
    </section>
  );
};

export default Hero;
